// ═══════════════════════════════════════════════════════════════
// PA Grants — Applicant Portal: My Projects
// Filtered project list with create (auto-scoped to user's org)
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, SimpleGrid, Loader, Center, Table, Progress,
} from '@mantine/core';
import {
  IconPlus, IconSearch, IconExternalLink, IconRefresh, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { exportToCSV } from '../../../lib/exportUtils';
import { useMyPAOrg } from '../../../hooks/useMyPAOrg';
import { useMyPAProjects, useMyPADocuments } from '../../../hooks/useMyPAOrgData';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useRoleStore } from '../../../store/roleStore';
import type { DamageCategory, ProjectPhase } from '../../../types/pagrants';
import {
  PROJECT_PHASE_LABELS, PROJECT_STATUS_LABELS,
  DAMAGE_CATEGORY_LABELS,
} from '../../../types/pagrants';

const PHASE_COLORS: Record<string, string> = {
  formulation: 'blue', review: 'yellow', obligation: 'green',
  work_in_progress: 'orange', closeout: 'violet', appeal: 'red',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray', in_review: 'yellow', approved: 'green', obligated: 'teal',
  in_progress: 'blue', completed: 'green', closed: 'gray', appealed: 'red',
};

const CATEGORY_OPTIONS = (Object.entries(DAMAGE_CATEGORY_LABELS) as Array<[DamageCategory, string]>)
  .map(([v, l]) => ({ value: v, label: l }));

const fmt = (v: string) => {
  const n = parseFloat(v);
  return isNaN(n) ? '--' : '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

export default function PortalProjects() {
  const navigate = useNavigate();
  const myOrg = useMyPAOrg();
  const myProjects = useMyPAProjects();
  const myDocs = useMyPADocuments();
  const { disasters, loadingProjects, createProject, fetchProjects } = usePAGrantsStore();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';

  const [search, setSearch] = useState('');
  const [filterPhase, setFilterPhase] = useState<string>('all');
  const [showCreate, setShowCreate] = useState(false);

  // Create form state
  const [form, setForm] = useState({
    projectTitle: '', category: '' as string, projectSize: 'small',
    disasterId: '', damageDescription: '', scopeOfWork: '',
    estimatedCost: '',
  });

  // Data is fetched by ApplicantPortalLayout — no need to re-fetch here

  // Filter
  const filtered = useMemo(() => {
    let list = myProjects;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.projectTitle.toLowerCase().includes(q) ||
        p.projectNumber.toLowerCase().includes(q)
      );
    }
    if (filterPhase !== 'all') {
      list = list.filter(p => p.phase === filterPhase);
    }
    return list;
  }, [myProjects, search, filterPhase]);

  // Next PW number
  const nextPW = useMemo(() => {
    let max = 0;
    for (const p of myProjects) {
      const m = p.projectNumber.match(/PW-?(\d+)/i);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    return `PW-${String(max + 1).padStart(3, '0')}`;
  }, [myProjects]);

  async function handleCreate() {
    if (!form.projectTitle.trim() || !myOrg) return;
    try {
      await createProject({
        organizationId: String(myOrg.Rid),
        disasterId: form.disasterId,
        projectNumber: nextPW,
        projectTitle: form.projectTitle.trim(),
        category: form.category,
        projectSize: form.projectSize,
        damageDescription: form.damageDescription.trim(),
        scopeOfWork: form.scopeOfWork.trim(),
        estimatedCost: form.estimatedCost.replace(/[^0-9.]/g, ''),
        phase: 'formulation',
        status: 'draft',
      });
      notifications.show({ color: 'green', title: 'Created', message: `Project ${nextPW} created.` });
      setShowCreate(false);
      setForm({ projectTitle: '', category: '', projectSize: 'small', disasterId: '', damageDescription: '', scopeOfWork: '', estimatedCost: '' });
    } catch {
      notifications.show({ color: 'red', title: 'Error', message: 'Failed to create project.' });
    }
  }

  if (loadingProjects) {
    return <Center p="xl"><Loader size="sm" /></Center>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>My Projects</Title>
        <Group gap="xs">
          <Button variant="light" size="xs" leftSection={<IconDownload size={16} />} onClick={() => {
            exportToCSV('my-pa-projects.csv',
              ['Project #', 'Title', 'Category', 'Size', 'Phase', 'Status', 'Estimated Cost'],
              filtered.map(p => [p.projectNumber, p.projectTitle, p.category, p.projectSize, p.phase, p.status, p.estimatedCost])
            );
          }}>Export CSV</Button>
          <Button variant="subtle" size="xs" leftSection={<IconRefresh size={14} />}
            onClick={() => fetchProjects()}>Refresh</Button>
          {!isViewer && (
            <Button size="xs" leftSection={<IconPlus size={14} />}
              onClick={() => setShowCreate(true)}>New Project</Button>
          )}
        </Group>
      </Group>

      <Group gap="xs">
        <TextInput
          placeholder="Search projects..."
          leftSection={<IconSearch size={14} />}
          size="xs" value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Select
          size="xs" data={[{ value: 'all', label: 'All Phases' }, ...Object.entries(PROJECT_PHASE_LABELS).map(([v, l]) => ({ value: v, label: l }))]}
          value={filterPhase} onChange={v => setFilterPhase(v ?? 'all')}
          style={{ width: 160 }}
        />
      </Group>

      <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>PW #</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Phase</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Completion</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Estimated</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Obligated</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Docs</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={11}>
                  <Text c="dimmed" ta="center" py="md" size="sm">
                    {search || filterPhase !== 'all' ? 'No matching projects' : 'No projects yet'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : filtered.map(p => {
              const pctNum = parseFloat(p.completionPctEstimate) || 0;
              const docCount = myDocs.filter(d => d.projectId === String(p.Rid)).length;
              return (
                <Table.Tr key={p.Rid} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/pagrants/pa/projects/${p.Rid}`)}>
                  <Table.Td><Text size="sm" fw={600}>{p.projectNumber}</Text></Table.Td>
                  <Table.Td><Text size="sm">{p.projectTitle}</Text></Table.Td>
                  <Table.Td>
                    {p.category && (
                      <Badge size="xs" variant="light">
                        {DAMAGE_CATEGORY_LABELS[p.category as DamageCategory] ?? p.category}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={PHASE_COLORS[p.phase] || 'gray'} variant="light">
                      {PROJECT_PHASE_LABELS[p.phase as ProjectPhase] ?? p.phase}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={STATUS_COLORS[p.status] || 'gray'} variant="dot">
                      {PROJECT_STATUS_LABELS[p.status as keyof typeof PROJECT_STATUS_LABELS] ?? p.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ minWidth: 90 }}>
                    <Group gap={4}>
                      <Progress value={pctNum} size="sm" style={{ flex: 1 }} color={pctNum >= 100 ? 'green' : 'blue'} />
                      <Text size="xs" c="dimmed">{pctNum}%</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{fmt(p.estimatedCost)}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{fmt(p.obligatedAmount)}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Badge size="xs" variant="light">{docCount}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <IconExternalLink size={14} color="var(--mantine-color-dimmed)" />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Create modal */}
      <Modal opened={showCreate} onClose={() => setShowCreate(false)} title="New Project" size="lg">
        <Stack gap="sm">
          <TextInput label="Project Title" required value={form.projectTitle}
            onChange={e => setForm(f => ({ ...f, projectTitle: e.currentTarget.value }))} />
          <SimpleGrid cols={2} spacing="sm">
            <Select label="Disaster" data={disasters.map(d => ({ value: String(d.Rid), label: d.disasterName }))}
              value={form.disasterId || null} onChange={v => setForm(f => ({ ...f, disasterId: v ?? '' }))}
              placeholder="Select disaster" searchable />
            <Select label="Category" data={CATEGORY_OPTIONS}
              value={form.category || null} onChange={v => setForm(f => ({ ...f, category: v ?? '' }))}
              placeholder="Select category" />
            <Select label="Project Size" data={[{ value: 'small', label: 'Small' }, { value: 'large', label: 'Large' }]}
              value={form.projectSize} onChange={v => setForm(f => ({ ...f, projectSize: v ?? 'small' }))} />
            <TextInput label="Estimated Cost" value={form.estimatedCost}
              onChange={e => setForm(f => ({ ...f, estimatedCost: e.currentTarget.value }))}
              placeholder="$0" />
          </SimpleGrid>
          <Textarea label="Damage Description" value={form.damageDescription}
            onChange={e => setForm(f => ({ ...f, damageDescription: e.currentTarget.value }))}
            rows={2} />
          <Textarea label="Scope of Work" value={form.scopeOfWork}
            onChange={e => setForm(f => ({ ...f, scopeOfWork: e.currentTarget.value }))}
            rows={2} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Project</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
